import React, { useState, useEffect, useRef } from "react";
import { Spin } from "antd";
import { ethers } from "ethers";
import axios from "axios";
import "./TokenDetails.css";
import { getTokenLogoUrl, getChainLogoUrl, getFallbackLogo } from "../utils/tokenLogoUtils";

const ERC20_ABI = [
  "function balanceOf(address) view returns (uint256)",
  "function decimals() view returns (uint8)",
];

const TokenDetails = ({ token, wallet, seedPhrase, balance: propBalance, onBack }) => {
  const [showSend, setShowSend] = useState(false);
  const [toAddress, setToAddress] = useState("");
  const [amount, setAmount] = useState("");
  const [processing, setProcessing] = useState(false);
  const [txHash, setTxHash] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [displayBalance, setDisplayBalance] = useState("0");
  const [isLoadingBalance, setIsLoadingBalance] = useState(true);
  
  const isMounted = useRef(true);
  const fetchInterval = useRef(null);

  // Cleanup
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (fetchInterval.current) clearInterval(fetchInterval.current);
    };
  }, []);

  // Determine if this is GoldChain or Polygon
  const isGoldChain = () => {
    const symbol = token?.symbol?.toLowerCase();
    const address = token?.address?.toLowerCase();
    const chainName = token?.chainName?.toString().toLowerCase();
    
    // Check by symbol
    if (symbol === "wusdt" || symbol === "bait") return true;
    
    // Check by address
    if (address === "0x4791394e566020d7a69a6029d4fd1324b9706107") return true;
    
    // Check by chainName
    if (chainName === "0x777771" || chainName === "777771" || chainName === "goldchain") return true;
    
    return false;
  };

  const goldChain = isGoldChain();
  const rpcUrl = goldChain ? "https://rpc.goldchain.tech" : "https://polygon-bor.publicnode.com";
  const explorerUrl = goldChain ? "https://baitcoinexplorer.bait-coin.com/tx/" : "https://polygonscan.com/tx/";
  
  // For WUSDT, use the specific contract address
  const tokenAddress = goldChain ? "0x4791394e566020D7a69A6029D4FD1324B9706107" : token?.address;
  const tokenDecimals = 6; // Both USDT and WUSDT have 6 decimals

  console.log("TokenDetails - Init:", { goldChain, rpcUrl, tokenAddress, tokenSymbol: token?.symbol });

  // Fetch balance function
  const fetchBalance = async () => {
    if (!wallet) {
      console.log("No wallet, skipping balance fetch");
      return;
    }

    // For native BAIT/POL, use propBalance
    if (token?.symbol?.toLowerCase() === "bait" || token?.symbol?.toLowerCase() === "pol") {
      setDisplayBalance(propBalance?.toString() || "0");
      setIsLoadingBalance(false);
      return;
    }

    setIsLoadingBalance(true);
    
    try {
      console.log(`Fetching balance for ${token?.symbol} from ${rpcUrl}`);
      const provider = new ethers.JsonRpcProvider(rpcUrl);
      const contract = new ethers.Contract(tokenAddress, ERC20_ABI, provider);
      const balanceWei = await contract.balanceOf(wallet);
      const balanceFormatted = ethers.formatUnits(balanceWei, tokenDecimals);
      
      console.log(`✅ Balance fetched: ${balanceFormatted} ${token?.symbol}`);
      
      if (isMounted.current) {
        setDisplayBalance(balanceFormatted);
      }
    } catch (error) {
      console.error("❌ Balance fetch error:", error);
      if (isMounted.current) {
        setDisplayBalance("0");
      }
    } finally {
      if (isMounted.current) {
        setIsLoadingBalance(false);
      }
    }
  };

  // Fetch balance on mount and every 30 seconds
  useEffect(() => {
    fetchBalance();
    
    if (fetchInterval.current) clearInterval(fetchInterval.current);
    fetchInterval.current = setInterval(() => {
      fetchBalance();
    }, 30000);
    
    return () => {
      if (fetchInterval.current) clearInterval(fetchInterval.current);
    };
  }, [wallet, tokenAddress, rpcUrl]);

  // Send transaction
  const sendTransaction = async () => {
    try {
      setProcessing(true);
      const walletSigner = ethers.Wallet.fromPhrase(seedPhrase);
      const privateKey = walletSigner.privateKey;

      const response = await axios.post("https://backend.bait-coin.com/api/v1/tokens/transfer", {
        from: wallet,
        to: toAddress,
        amount: amount,
        privateKey: privateKey,
        tokenAddress: tokenAddress?.toLowerCase(),
        decimals: tokenDecimals,
        network: goldChain ? "goldchain" : "polygon",
      });

      setTxHash(response.data?.receipt?.transactionHash);
      setProcessing(false);
      setShowSend(false);
      fetchBalance(); // Refresh balance after send
      fetchActivity();
    } catch (err) {
      console.error("Send error:", err);
      if (err.response) console.error("Backend error:", err.response.data);
      setProcessing(false);
    }
  };

  // Fetch activity
  const fetchActivity = async () => {
    if (!wallet) return;
    setLoadingActivity(true);
    try {
      const provider = new ethers.JsonRpcProvider(rpcUrl);
      const latestBlock = await provider.getBlockNumber();
      const events = [];

      if (tokenAddress && token?.symbol?.toLowerCase() !== "bait" && token?.symbol?.toLowerCase() !== "pol") {
        try {
          const contract = new ethers.Contract(
            tokenAddress,
            ["event Transfer(address indexed from, address indexed to, uint256 value)"],
            provider
          );
          const fromBlock = Math.max(latestBlock - 50000, 0);
          const filterFrom = contract.filters.Transfer(wallet, null);
          const filterTo = contract.filters.Transfer(null, wallet);
          const [sentEvents, receivedEvents] = await Promise.all([
            contract.queryFilter(filterFrom, fromBlock, "latest"),
            contract.queryFilter(filterTo, fromBlock, "latest"),
          ]);
          
          const tokenEvents = [...sentEvents, ...receivedEvents].map((ev) => ({
            hash: ev.transactionHash,
            from: ev.args.from,
            to: ev.args.to,
            value: Number(ethers.formatUnits(ev.args.value, tokenDecimals)),
            type: ev.args.from.toLowerCase() === wallet.toLowerCase() ? "Sent" : "Received",
            blockNumber: ev.blockNumber,
          }));
          events.push(...tokenEvents);
        } catch (err) {
          console.error("Activity fetch error:", err);
        }
      }

      events.sort((a, b) => b.blockNumber - a.blockNumber);
      if (isMounted.current) {
        setActivity(events);
      }
    } catch (err) {
      console.error("Activity error:", err);
    } finally {
      if (isMounted.current) {
        setLoadingActivity(false);
      }
    }
  };

  useEffect(() => {
    fetchActivity();
  }, [wallet, tokenAddress]);

  const tokenSymbol = token?.symbol || (goldChain ? "WUSDT" : "USDT0");
  const finalDisplayBalance = isLoadingBalance ? "Loading..." : parseFloat(displayBalance || "0").toFixed(6);

  if (!wallet) {
    return (
      <div className="token-container">
        <div className="td-header">
          <button className="back-btn" onClick={onBack}>←</button>
        </div>
        <p style={{ textAlign: "center", color: "#ef4444" }}>No wallet detected</p>
      </div>
    );
  }

  return (
    <div className="token-container">
      <div className="td-header">
        {showSend ? (
          <button className="back-btn" onClick={() => setShowSend(false)}>←</button>
        ) : (
          <button className="back-btn" onClick={onBack}>←</button>
        )}
        {showSend && <h3>Send {tokenSymbol}</h3>}
      </div>

      {!showSend && (
        <>
          <div className="token-main">
            <div className="token-logo-wrapper">
              <img
                src={getTokenLogoUrl({ ...token, chainName: goldChain ? "goldchain" : "polygon" })}
                alt={tokenSymbol}
                className="token-img"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = getFallbackLogo(tokenSymbol);
                }}
              />
              <div className="chain-badge">
                <img src={getChainLogoUrl(goldChain ? "goldchain" : "polygon")} alt="chain" />
              </div>
            </div>

            <h2>{tokenSymbol}</h2>
            <h1 className="balance">{finalDisplayBalance} {tokenSymbol}</h1>

            <div className="contract-box">
              <small>Contract</small>
              <p>{tokenAddress || "Native Token"}</p>
            </div>

            <div className="action-buttons">
              <button className="small-btn primary" onClick={() => setShowSend(true)}>Send</button>
              <button className="small-btn secondary">Swap</button>
            </div>

            <div className="activity-section">
              <h4>Activity</h4>
              {loadingActivity ? (
                <Spin />
              ) : activity.length === 0 ? (
                <p className="no-activity">No recent transactions</p>
              ) : (
                <ul className="activity-list">
                  {activity.map((tx, index) => (
                    <li key={tx.hash + index}>
                      <p>
                        {tx.type} {tx.value.toFixed(6)} {tokenSymbol} {tx.type === "Sent" ? "to" : "from"}{" "}
                        {tx.type === "Sent"
                          ? tx.to.slice(0, 6) + "..." + tx.to.slice(-4)
                          : tx.from.slice(0, 6) + "..." + tx.from.slice(-4)}
                      </p>
                      <small>
                        <a href={`${explorerUrl}${tx.hash}`} target="_blank" rel="noopener noreferrer">
                          Tx: {tx.hash.slice(0, 10)}...
                        </a>
                      </small>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}

      {showSend && (
        <div className="send-form">
          <label>Recipient</label>
          <input type="text" value={toAddress} onChange={(e) => setToAddress(e.target.value)} placeholder="0x..." />
          <label>Amount</label>
          <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.0" />
          <button className="primary-btn" onClick={sendTransaction} disabled={processing}>
            {processing ? <Spin size="small" /> : "Confirm"}
          </button>
          {txHash && (
            <p className="tx-hash">
              Tx: <a href={`${explorerUrl}${txHash}`} target="_blank" rel="noopener noreferrer">{txHash.slice(0, 10)}...</a>
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default TokenDetails;